import { useState, useRef, useEffect, useCallback } from 'react';
import './ChatWidget.css';

const BOT_ENDPOINT = import.meta.env.VITE_CHATBOT_URL || '/chatbot';

function TypingDots() {
  return (
    <div className="cw-bubble cw-bubble--bot cw-typing">
      <span /><span /><span />
    </div>
  );
}

function Message({ msg }) {
  return (
    <div className={`cw-bubble cw-bubble--${msg.role}`}>
      <div className="cw-bubble-text">{msg.content}</div>
    </div>
  );
}

export default function ChatWidget() {
  const [enabled, setEnabled] = useState(null); // null = checking, true/false = resolved
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hi! I\'m Eva, the RDS Studio assistant. How can I help you today?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const sessionId = useRef(`sess_${Date.now()}`);

  // Check if chatbot is enabled on mount
  useEffect(() => {
    fetch(`${BOT_ENDPOINT}/health`)
      .then(r => r.json())
      .then(d => setEnabled(d.enabled !== false))
      .catch(() => setEnabled(true)); // if health check fails, show widget anyway
  }, []);

  // Scroll to latest message
  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading, open]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const sendMessage = useCallback(async (text) => {
    const question = (text || input).trim();
    if (!question || loading) return;
    setInput('');
    setError('');
    setMessages(prev => [...prev, { role: 'user', content: question }]);
    setLoading(true);

    // Build history excluding the initial greeting
    const history = messages.slice(1).map(m => ({ role: m.role, content: m.content }));

    try {
      const res = await fetch(`${BOT_ENDPOINT}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, history, session_id: sessionId.current }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.answer }]);
      if (!open) setUnread(n => n + 1);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, open]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickReplies = [
    'What services do you offer?',
    'How much does a website cost?',
    'How do I get started?',
  ];

  // Don't render until health check resolves, or if disabled by admin
  if (!enabled) return null;

  return (
    <div className="cw-wrap">
      {/* Chat panel */}
      <div className={`cw-panel${open ? ' cw-panel--open' : ''}`} role="dialog" aria-label="Chat with Eva">
        {/* Header */}
        <div className="cw-header">
          <div className="cw-header-info">
            <div className="cw-avatar">E</div>
            <div>
              <div className="cw-header-name">Eva</div>
              <div className="cw-header-sub">RDS Studio AI Assistant</div>
            </div>
          </div>
          <button className="cw-close" onClick={() => setOpen(false)} aria-label="Close chat">✕</button>
        </div>

        {/* Messages */}
        <div className="cw-messages">
          {messages.map((m, i) => <Message key={i} msg={m} />)}
          {loading && <TypingDots />}
          {error && <div className="cw-error">{error}</div>}
          <div ref={bottomRef} />
        </div>

        {/* Quick replies — shown only when no user messages yet */}
        {messages.length === 1 && (
          <div className="cw-quick-replies">
            {quickReplies.map(q => (
              <button key={q} className="cw-quick-btn" onClick={() => sendMessage(q)}>{q}</button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="cw-input-row">
          <textarea
            ref={inputRef}
            className="cw-input"
            placeholder="Type a message…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            maxLength={1000}
            disabled={loading}
          />
          <button
            className="cw-send"
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            aria-label="Send"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>

      {/* Bubble toggle button */}
      <button
        className={`cw-trigger${open ? ' cw-trigger--open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Close chat' : 'Chat with Rudi'}
      >
        {open ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        )}
        {!open && unread > 0 && <span className="cw-badge">{unread}</span>}
      </button>
    </div>
  );
}
